/**
 * Persistence for the token pair. Deliberately framework-agnostic: no React, no
 * Next imports, and every function degrades to a no-op during SSR so it can be
 * called from module scope or effects without guards at each call site.
 */

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  /** ISO-8601 expiry of the access token. */
  expiresAt: string;
}

const STORAGE_KEY = "blink.business-os.session";

function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    // Blocked by privacy settings or a sandboxed iframe.
    return null;
  }
}

function isStoredSession(value: unknown): value is StoredSession {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.accessToken === "string" &&
    typeof candidate.refreshToken === "string" &&
    typeof candidate.expiresAt === "string"
  );
}

export function readSession(): StoredSession | null {
  const storage = getStorage();
  if (!storage) return null;

  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredSession(parsed)) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function writeSession(session: StoredSession): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Quota exhausted — the in-memory session still works for this tab.
  }
}

export function clearSession(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing actionable.
  }
}

/**
 * `skewSeconds` buys time for the request to reach the server before the token
 * actually lapses. A missing or unparseable session counts as expired.
 */
export function isExpired(session: StoredSession | null, skewSeconds = 30): boolean {
  if (!session) return true;
  const expiresAt = Date.parse(session.expiresAt);
  if (Number.isNaN(expiresAt)) return true;
  return expiresAt - skewSeconds * 1000 <= Date.now();
}

/**
 * Notifies on sign-in/sign-out that happened in another tab, so an ERP left
 * open in several tabs does not keep one of them on a dead session.
 */
export function subscribeToSession(
  listener: (session: StoredSession | null) => void,
): () => void {
  if (typeof window === "undefined") return () => {};

  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    listener(readSession());
  };

  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}
